import {
  AcademicYear,
  CloneClassData,
  CreateAcademicYearRequest,
  UpdateAcademicYearRequest,
} from '../../types/academicYear';
import { ApiResponse } from '../../types/common';
import { axiosInstance } from './axios';

export const academicYearService = {
  getAll: async (instituteId: number): Promise<AcademicYear[]> => {
    const response = await axiosInstance.get<ApiResponse<AcademicYear[]>>(
      'v1/AcademicYears',
      { params: { instituteId } }
    );
    if (!response.data.succeeded) {
      throw new Error(response.data.messages?.[0] || 'Failed to fetch academic years');
    }
    return response.data.data;
  },

  getById: async (id: number, instituteId: number): Promise<AcademicYear> => {
    const response = await axiosInstance.get<ApiResponse<AcademicYear>>(
      `v1/AcademicYears/${id}`,
      { params: { instituteId } }
    );
    if (!response.data.succeeded) {
      throw new Error(response.data.messages?.[0] || 'Failed to fetch academic year');
    }
    return response.data.data;
  },

  getActive: async (instituteId: number): Promise<AcademicYear | null> => {
    const response = await axiosInstance.get<ApiResponse<AcademicYear>>(
      'v1/AcademicYears/active',
      { params: { instituteId } }
    );
    if (!response.data.succeeded || !response.data.data) {
      return null;
    }
    return response.data.data;
  },

  create: async (request: CreateAcademicYearRequest): Promise<number> => {
    const response = await axiosInstance.post<ApiResponse<number>>(
      'v1/AcademicYears',
      request
    );
    if (!response.data.succeeded) {
      throw new Error(response.data.messages?.[0] || 'Failed to create academic year');
    }
    return response.data.data;
  },

  update: async (request: UpdateAcademicYearRequest): Promise<void> => {
    const response = await axiosInstance.put<ApiResponse<string>>(
      `v1/AcademicYears/${request.id}`,
      request
    );
    if (!response.data.succeeded) {
      throw new Error(response.data.messages?.[0] || 'Failed to update academic year');
    }
  },

  setDefault: async (id: number, instituteId: number): Promise<void> => {
    const response = await axiosInstance.put<ApiResponse<string>>(
      `v1/AcademicYears/${id}/setDefault`,
      { instituteId }
    );
    if (!response.data.succeeded) {
      throw new Error(response.data.messages?.[0] || 'Failed to set default academic year');
    }
  },

  activate: async (id: number): Promise<void> => {
    const response = await axiosInstance.put<ApiResponse<string>>(
      `v1/AcademicYears/${id}/activate`
    );
    if (!response.data.succeeded) {
      throw new Error(response.data.messages?.[0] || 'Failed to activate academic year');
    }
  },

  complete: async (id: number): Promise<void> => {
    const response = await axiosInstance.put<ApiResponse<string>>(
      `v1/AcademicYears/${id}/complete`
    );
    if (!response.data.succeeded) {
      throw new Error(response.data.messages?.[0] || 'Failed to complete academic year');
    }
  },

  archive: async (id: number): Promise<void> => {
    const response = await axiosInstance.put<ApiResponse<string>>(
      `v1/AcademicYears/${id}/archive`
    );
    if (!response.data.succeeded) {
      throw new Error(response.data.messages?.[0] || 'Failed to archive academic year');
    }
  },

  delete: async (id: number): Promise<void> => {
    const response = await axiosInstance.delete<ApiResponse<string>>(
      `v1/AcademicYears/${id}`
    );
    if (!response.data.succeeded) {
      throw new Error(response.data.messages?.[0] || 'Failed to delete academic year');
    }
  },

  cloneClassData: async (yearId: number, classes: CloneClassData[]): Promise<void> => {
    const response = await axiosInstance.post<ApiResponse<string>>(
      `v1/AcademicYears/${yearId}/clone-class-data`,
      { academicYearId: yearId, classes }
    );
    if (!response.data.succeeded) {
      throw new Error(response.data.messages?.[0] || 'Failed to clone class data');
    }
  },

  getCurrent: async (): Promise<AcademicYear | null> => {
    const response = await axiosInstance.get<ApiResponse<AcademicYear>>(
      'v1/AcademicYears/current'
    );
    if (!response.data.succeeded || !response.data.data) {
      return null;
    }
    return response.data.data;
  },

  lock: async (id: number): Promise<void> => {
    const response = await axiosInstance.put<ApiResponse<string>>(
      `v1/AcademicYears/${id}/lock`
    );
    if (!response.data.succeeded) {
      throw new Error(response.data.messages?.[0] || 'Failed to lock academic year');
    }
  },

  unlock: async (id: number): Promise<void> => {
    const response = await axiosInstance.put<ApiResponse<string>>(
      `v1/AcademicYears/${id}/unlock`
    );
    if (!response.data.succeeded) {
      throw new Error(response.data.messages?.[0] || 'Failed to unlock academic year');
    }
  },

  autoClone: async (targetYearId: number, sourceYearId: number): Promise<void> => {
    const response = await axiosInstance.post<ApiResponse<string>>(
      `v1/AcademicYears/${targetYearId}/auto-clone`,
      { sourceYearId }
    );
    if (!response.data.succeeded) {
      throw new Error(response.data.messages?.[0] || 'Failed to auto-clone academic year data');
    }
  },
};
